import React, { useState } from 'react';
import ContentFilters, { ContentPagination } from '../../components/ContentFilters/ContentFilters';
import useContentList from '../../components/ContentFilters/useContentList';
import { Container, Table, Button, Modal, Form, Image, Toast } from 'react-bootstrap';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { getAnnouncements, deleteAnnouncement, addAnnouncement, updateAnnouncement } from '../../services/announcementService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AnnouncementList = () => {
  const { items: announcements, loading, setLoading, error, setError,
    reload: fetchAnnouncements, filterProps, paginationProps } = useContentList(getAnnouncements);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [newAnnouncement, setNewAnnouncement] = useState({
    header: '',
    detail: '',
    date: '',
    picture: null,
    file: null
  });
  const [editAnnouncement, setEditAnnouncement] = useState({
    guid: '',
    header: '',
    detail: '',
    date: '',
    picture: null,
    file: null,
    pictureBase64: '',
    fileBase64: '',
    pictureUrl: '',
    fileUrl: ''
  });


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleDelete = async () => {
    if (!selectedAnnouncement) return;
    
    try {
      setLoading(true);
      const response = await deleteAnnouncement(selectedAnnouncement.guid);
      
      if (response?.isSuccess) {
        await fetchAnnouncements();
        setShowDeleteModal(false);
      } else {
        setError('Duyuru silinirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Duyuru silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (announcement) => {
    setSelectedAnnouncement(announcement);
    setEditAnnouncement({
      guid: announcement.guid,
      header: announcement.header,
      detail: announcement.detail,
      date: announcement.date,
      picture: null,
      file: null,
      pictureBase64: '',
      fileBase64: '',
      pictureUrl: announcement.pictureUrl || '',
      fileUrl: announcement.fileUrl || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Mevcut duyuru verilerini kullanarak güncelleme verilerini oluştur
      const updateData = { 
        ...editAnnouncement,
        date: editAnnouncement.date ? new Date(editAnnouncement.date).toISOString() : new Date().toISOString()
      };
      
      // Eğer yeni bir resim seçilmemişse ve mevcut resim varsa, mevcut resmi kullan
      if (!updateData.picture && selectedAnnouncement?.pictureUrl) {
        // Mevcut resim bilgisini koru
        updateData.existingPictureUrl = selectedAnnouncement.pictureUrl;
        console.log('Mevcut resim korunuyor:', selectedAnnouncement.pictureUrl);
      } else if (!updateData.picture && !selectedAnnouncement?.pictureUrl) {
        // Eğer resim seçilmemiş ve mevcut resim de yoksa, boş gönder
        updateData.picture = null;
        updateData.pictureBase64 = '';
        updateData.existingPictureUrl = '';
        console.log('Resim seçilmedi ve mevcut resim yok, boş gönderiliyor');
      }
      
      // Eğer yeni bir dosya seçilmemişse ve mevcut dosya varsa, mevcut dosyayı kullan
      if (!updateData.file && selectedAnnouncement?.fileUrl) {
        // Mevcut dosya bilgisini koru
        updateData.existingFileUrl = selectedAnnouncement.fileUrl;
        console.log('Mevcut dosya korunuyor:', selectedAnnouncement.fileUrl);
      } else if (!updateData.file && !selectedAnnouncement?.fileUrl) {
        // Eğer dosya seçilmemiş ve mevcut dosya da yoksa, boş gönder
        updateData.file = null;
        updateData.fileBase64 = '';
        updateData.existingFileUrl = '';
        console.log('Dosya seçilmedi ve mevcut dosya yok, boş gönderiliyor');
      }
      
      console.log('Güncelleme için gönderilen veri:', {
        ...updateData,
        picture: updateData.picture ? 'Yeni resim seçildi' : 'Resim seçilmedi',
        file: updateData.file ? 'Yeni dosya seçildi' : 'Dosya seçilmedi',
        existingPictureUrl: updateData.existingPictureUrl || 'Yok',
        existingFileUrl: updateData.existingFileUrl || 'Yok'
      });
      
      const response = await updateAnnouncement(updateData);
      
      // Log the full response structure to debug
      console.log('Update response structure:', response);
      
      // Check different possible success response formats
      if (response?.isSuccess || response?.data?.isSuccess || response === true || response?.data === true) {
        setToastMessage('Duyuru başarıyla güncellendi!');
        setToastVariant('success');
        setShowToast(true);
        await fetchAnnouncements();
        setShowEditModal(false);
        setError(null);
      } else {
        const errorMessage = response?.resultMessage || response?.data?.resultMessage || 'Duyuru güncellenirken bir hata oluştu.';
        setToastMessage(errorMessage);
        setToastVariant('danger');
        setShowToast(true);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Update error:', err);
      setError('Duyuru güncellenirken bir hata oluştu.');
      setToastMessage('Duyuru güncellenirken bir hata oluştu.');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const data = {
        date: newAnnouncement.date ? new Date(newAnnouncement.date).toISOString() : new Date().toISOString(),
        header: newAnnouncement.header,
        detail: newAnnouncement.detail,
        picture: newAnnouncement.picture,
        file: newAnnouncement.file
      };

      console.log('Sending announcement data:', {
        ...data,
        picture: data.picture ? data.picture.name : null,
        file: data.file ? data.file.name : null
      });

      const response = await addAnnouncement(data);
      
      if (response?.isSuccess) {
        await fetchAnnouncements();
        setShowAddModal(false);
        setNewAnnouncement({ header: '', detail: '', date: '', picture: null, file: null });
      } else {
        setError('Duyuru eklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Add error:', err);
      setError('Duyuru eklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Container className="mt-4">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Duyurular</h2>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          Yeni Duyuru Ekle
        </Button>
      </div>

      <ContentFilters {...filterProps} />

      <Table striped bordered hover responsive aria-busy={loading}>
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Başlık</th>
            <th>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={3} className="text-center py-4" role="status">Yükleniyor…</td></tr>}
          {!loading && announcements.length === 0 && <tr><td colSpan={3} className="text-center py-4">Filtrelere uygun kayıt bulunamadı.</td></tr>}
          {!loading && announcements.map((announcement) => (
            <tr key={announcement.guid}>
              <td>{formatDate(announcement.date)}</td>
              <td>{announcement.header}</td>
              <td>
                <Button 
                  variant="info" 
                  size="sm" 
                  className="me-2"
                  onClick={() => {
                    setSelectedAnnouncement(announcement);
                    setShowDetailModal(true);
                  }}
                >
                  <FaEye className="me-1" /> Detay
                </Button>
                <Button 
                  variant="primary" 
                  size="sm" 
                  className="me-2"
                  onClick={() => handleEdit(announcement)}
                >
                  <FaEdit className="me-1" /> Düzenle
                </Button>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => {
                    setSelectedAnnouncement(announcement);
                    setShowDeleteModal(true);
                  }}
                >
                  <FaTrash className="me-1" /> Sil
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ContentPagination {...paginationProps} />

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Duyuru Detayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAnnouncement && (
            <>
              <h4>{selectedAnnouncement.header}</h4>
              <p className="text-muted">{formatDate(selectedAnnouncement.date)}</p>
              
              {selectedAnnouncement.pictureUrl && (
                <div className="text-center mb-3">
                  <Image 
                    src={selectedAnnouncement.pictureUrl} 
                    alt={selectedAnnouncement.header}
                    fluid 
                  />
                </div>
              )}
              
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {selectedAnnouncement.detail}
              </div>
              
              {selectedAnnouncement.fileUrl && (
                <div className="mt-3">
                  <a 
                    href={selectedAnnouncement.fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                  >
                    Dosyayı İndir
                  </a>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Duyuru Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu duyuruyu silmek istediğinizden emin misiniz?
          <br />
          <strong>{selectedAnnouncement?.header}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton>
          <Modal.Title>Yeni İlan Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: 'auto', maxHeight: '90vh' }}>
          <Form onSubmit={handleAdd}>
            <Form.Group className="mb-3">
              <Form.Label>Başlık</Form.Label>
              <Form.Control
                type="text"
                value={newAnnouncement.header}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, header: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tarih</Form.Label>
              <Form.Control
                type="date"
                value={newAnnouncement.date}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-4" style={{ marginBottom: '2rem' }}>
              <Form.Label>Detay</Form.Label>
              <div style={{ width: '100%', marginBottom: '60px' }}>
                <ReactQuill
                  theme="snow"
                  value={newAnnouncement.detail || ''}
                  onChange={(value) => setNewAnnouncement(prev => ({ ...prev, detail: value }))}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                      [{ 'font': [] }],
                      [{ 'size': ['small', false, 'large', 'huge'] }],
                      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'script': 'sub'}, { 'script': 'super' }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                      [{ 'align': [] }],
                      ['link', 'image', 'video'],
                      ['clean'],
                      ['code-block']
                    ]
                  }}
                  style={{ 
                    height: '400px',
                    backgroundColor: 'var(--arc-surface)',
                    color: 'var(--arc-text-primary)'
                  }}
                  formats={[
                    'header', 'font', 'size',
                    'bold', 'italic', 'underline', 'strike', 'blockquote',
                    'color', 'background',
                    'script',
                    'list', 'bullet', 'indent',
                    'align',
                    'link', 'image', 'video',
                    'code-block'
                  ]}
                />
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Resim</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, picture: e.target.files[0] }))}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Dosya</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, file: e.target.files[0] }))}
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowAddModal(false)}>
                İptal
              </Button>
              <Button variant="primary" type="submit">
                Ekle
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Duyuru Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Başlık</Form.Label>
              <Form.Control
                type="text"
                value={editAnnouncement.header}
                onChange={(e) => setEditAnnouncement(prev => ({ ...prev, header: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tarih</Form.Label>
              <Form.Control
                type="date"
                value={editAnnouncement.date ? (() => {
                  try {
                    const date = new Date(editAnnouncement.date);
                    return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
                  } catch {
                    return '';
                  }
                })() : ''}
                onChange={(e) => setEditAnnouncement(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Detay</Form.Label>
              <ReactQuill
                theme="snow"
                value={editAnnouncement.detail}
                onChange={(value) => setEditAnnouncement(prev => ({ ...prev, detail: value }))}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                    [{ 'font': [] }],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'script': 'sub'}, { 'script': 'super' }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                    [{ 'align': [] }],
                    ['link', 'image', 'video'],
                    ['clean'],
                    ['code-block']
                  ]
                }}
                style={{ height: '400px', marginBottom: '50px' }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Resim</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setEditAnnouncement(prev => ({ ...prev, picture: e.target.files[0] }))}
              />
              {selectedAnnouncement?.pictureUrl && (
                <div className="mt-2">
                  <small>Mevcut resim: {selectedAnnouncement.pictureUrl}</small>
                </div>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Dosya</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setEditAnnouncement(prev => ({ ...prev, file: e.target.files[0] }))}
              />
              {selectedAnnouncement?.fileUrl && (
                <div className="mt-2">
                  <small>Mevcut dosya: {selectedAnnouncement.fileUrl}</small>
                </div>
              )}
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowEditModal(false)}>
                İptal
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Güncelleniyor...' : 'Güncelle'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Toast notification */}
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          minWidth: '250px'
        }}
        bg={toastVariant}
      >
        <Toast.Header closeButton>
          <strong className="me-auto">{toastVariant === 'success' ? 'Başarılı' : 'Hata'}</strong>
        </Toast.Header>
        <Toast.Body className={toastVariant === 'success' ? 'text-white' : ''}>
          {toastMessage}
        </Toast.Body>
      </Toast>
    </Container>
  );
};

export default AnnouncementList;
